import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import glassTheme from '../../styles/glassTheme';
import * as teacherApi from '../../services/teacherApi';
import * as topicsApi from '../../services/topicsApi';
import type { Course } from '../../services/teacherApi';
import type { Topic } from '../../services/topicsApi';
import { useAuthStore } from '../../store/authStore';
import { DashboardLayout } from '../../components/DashboardLayout';

// ==================== MODERN STYLED COMPONENTS ====================

// Portal Dropdown Component - renders dropdown in a portal to avoid parent event issues
const PortalDropdown: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  anchorElement: HTMLDivElement | null;
}> = ({ isOpen, onClose, children, anchorElement }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (isOpen && anchorElement) {
      const rect = anchorElement.getBoundingClientRect();
      setPosition({
        top: rect.bottom + 4,
        left: rect.right - 160 // Align to right edge
      });
    }
  }, [isOpen, anchorElement]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        anchorElement &&
        !anchorElement.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, anchorElement]);

  if (!isOpen || !anchorElement) return null;

  return createPortal(
    <PortalDropdownContainer
      ref={dropdownRef}
      style={{ top: position.top, left: position.left }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </PortalDropdownContainer>,
    document.body
  );
};

const PortalDropdownContainer = styled.div`
  position: fixed;
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 160px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 9999;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(96, 165, 250, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(96, 165, 250, 0.5);
    }
  }
`;

const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 1.5rem;
  height: calc(100vh - 120px);
  min-height: 600px;
`;

// Left Panel - Course Structure
const LeftPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-height: 0;
  overflow: hidden;
`;

// Header with course info
const CourseHeader = styled.div`
  padding: 1.25rem;
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
`;

const CourseTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 0.75rem;
`;

const CourseTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
  flex: 1;
`;

const StatusBadge = styled.span<{ $status: 'draft' | 'created' | 'published' }>`
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.$status) {
      case 'published': return 'rgba(34, 197, 94, 0.2)';
      case 'created': return 'rgba(251, 191, 36, 0.2)';
      default: return 'rgba(156, 163, 175, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'published': return '#22c55e';
      case 'created': return '#f59e0b';
      default: return '#9ca3af';
    }
  }};
`;

const CourseMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  font-size: 0.875rem;
  color: ${glassTheme.colors.text.secondary};
`;

const CourseCode = styled.code`
  padding: 0.25rem 0.75rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 6px;
  font-family: 'Monaco', 'Consolas', monospace;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(96, 165, 250, 0.2);
    border-color: ${glassTheme.colors.accent.blue};
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const IconButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' | 'success' }>`
  padding: 0.5rem 1rem;
  background: ${props => {
    if (props.$variant === 'danger') return 'rgba(239, 68, 68, 0.2)';
    if (props.$variant === 'success') return 'rgba(34, 197, 94, 0.2)';
    if (props.$variant === 'primary') return glassTheme.gradients.cyber;
    return 'rgba(0, 0, 0, 0.3)';
  }};
  border: 1px solid ${props => {
    if (props.$variant === 'danger') return 'rgba(239, 68, 68, 0.5)';
    if (props.$variant === 'success') return 'rgba(34, 197, 94, 0.5)';
    return glassTheme.colors.border;
  }};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

const EditIconButton = styled.button`
  padding: 0.4rem 0.6rem;
  background: rgba(96, 165, 250, 0.15);
  border: 1px solid rgba(96, 165, 250, 0.3);
  border-radius: 6px;
  color: ${glassTheme.colors.accent.blue};
  font-size: 1rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(96, 165, 250, 0.25);
    border-color: ${glassTheme.colors.accent.blue};
    transform: scale(1.05);
  }
`;

const BackLink = styled(Link)`
  padding: 0.5rem 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  text-decoration: none;
  display: inline-flex;
  align-items: center;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`;

// Course Structure Panel
const StructurePanel = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  overflow: hidden;
  border-radius: ${glassTheme.borderRadius.lg};
  overflow: visible;
`;

const StructureHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  background: rgba(0, 0, 0, 0.2);
`;

const StructureTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0;
`;

const ChapterCount = styled.span`
  padding: 0.25rem 0.75rem;
  background: rgba(96, 165, 250, 0.2);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
`;

const StructureContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(96, 165, 250, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(96, 165, 250, 0.5);
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  text-align: center;
  color: ${glassTheme.colors.text.muted};
`;

const ChapterList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const ChapterItem = styled.div<{ $isDragging?: boolean; $isOver?: boolean }>`
  background: ${props => props.$isOver ? 'rgba(59, 130, 246, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
  border: 2px solid ${props => {
    if (props.$isDragging) return glassTheme.colors.accent.blue;
    if (props.$isOver) return glassTheme.colors.accent.blue;
    return glassTheme.colors.border;
  }};
  border-radius: 12px;
  overflow: visible;
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${glassTheme.colors.borderHover};
  }
`;

const ChapterHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  cursor: pointer;
  background: rgba(0, 0, 0, 0.1);

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`;

const ChapterNumber = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: ${glassTheme.gradients.cyber};
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${glassTheme.colors.text.primary};
`;

const ChapterInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChapterTitle = styled.div`
  font-size: 0.95rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 0.25rem;
`;

const ChapterMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.75rem;
  color: ${glassTheme.colors.text.muted};
`;

const SmallIconBtn = styled.button`
  padding: 0.4rem;
  background: transparent;
  border: none;
  border-radius: 6px;
  color: ${glassTheme.colors.text.muted};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(96, 165, 250, 0.2);
    color: ${glassTheme.colors.accent.blue};
  }
`;

const ChapterContent = styled.div<{ $expanded: boolean }>`
  display: ${props => props.$expanded ? 'block' : 'none'};
  padding: 0 1rem 1rem;
  min-height: 80px;
`;

const SectionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-left: 2rem;
  padding-left: 1rem;
  padding-bottom: 100px;
  border-left: 2px solid ${glassTheme.colors.border};
`;

const SectionItem = styled.div<{ $isOver?: boolean }>`
  background: ${props => props.$isOver ? 'rgba(59, 130, 246, 0.15)' : 'rgba(0, 0, 0, 0.15)'};
  border: 1px dashed ${props => props.$isOver ? glassTheme.colors.accent.blue : glassTheme.colors.border};
  border-radius: 8px;
  padding: 0.75rem;
  transition: all ${glassTheme.transition.base};
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const SectionTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${glassTheme.colors.text.primary};
  flex: 1;
`;

const ChapterActions = styled.div`
  display: flex;
  gap: 0.25rem;
  opacity: 0;
  transition: opacity ${glassTheme.transition.base};

  ${ChapterHeader}:hover &,
  ${SectionHeader}:hover & {
    opacity: 1;
  }
`;

const TopicsContainer = styled.div<{ $isOver?: boolean }>`
  min-height: 60px;
  padding: 0.75rem;
  background: ${props => props.$isOver ? 'rgba(59, 130, 246, 0.15)' : 'rgba(0, 0, 0, 0.25)'};
  border: 2px dashed ${props => props.$isOver ? glassTheme.colors.accent.blue : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  transition: all ${glassTheme.transition.base};

  &:empty::before {
    content: '拖拽知识元到此处';
    color: ${glassTheme.colors.text.muted};
    font-size: 0.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    opacity: 0.6;
  }
`;

const TopicChip = styled.div<{ $isDragging?: boolean }>`
  padding: 0.4rem 0.5rem;
  background: ${props => props.$isDragging ? 'rgba(96, 165, 250, 0.4)' : 'rgba(59, 130, 246, 0.15)'};
  border: 1px solid ${glassTheme.colors.accent.blue};
  border-radius: 6px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.8rem;
  cursor: grab;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  transition: all ${glassTheme.transition.base};
  flex-shrink: 0;
  position: relative;

  &:hover {
    background: rgba(96, 165, 250, 0.25);
    transform: translateY(-1px);
  }

  &:active {
    cursor: grabbing;
  }
`;

const TopicRemoveBtn = styled.button`
  background: rgba(239, 68, 68, 0.1);
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  display: flex;
  font-size: 0.875rem;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(239, 68, 68, 0.2);
    transform: scale(1.1);
  }
`;

const TopicDragHandle = styled.span`
  cursor: grab;
  color: ${glassTheme.colors.text.muted};
  display: flex;
  align-items: center;
  padding: 0 2px;
  user-select: none;

  &:hover {
    color: ${glassTheme.colors.text.primary};
  }

  &:active {
    cursor: grabbing;
  }
`;

const TopicSequence = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${glassTheme.colors.accent.blue};
  min-width: 18px;
  text-align: center;
`;

// Activity Dropdown Components
const ActivityDropdownContainer = styled.div.attrs({ className: 'activity-dropdown-container' })`
  position: relative;
  display: inline-flex;
`;

const ActivityDropdownBtn = styled.button`
  background: rgba(96, 165, 250, 0.15);
  border: 1px solid rgba(96, 165, 250, 0.3);
  color: ${glassTheme.colors.accent.blue};
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  transition: all ${glassTheme.transition.base};
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(96, 165, 250, 0.25);
  }

  &:focus {
    outline: none;
  }

  &:active {
    background: rgba(96, 165, 250, 0.3);
  }
`;

const ActivityDropdownMenu = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  min-width: 180px;
  max-height: 300px;
  overflow-y: auto;
  z-index: 100;
  display: ${props => props.$isOpen ? 'block' : 'none'};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(96, 165, 250, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(96, 165, 250, 0.5);
    }
  }
`;

const ActivityDropdownItem = styled.button`
  width: 100%;
  padding: 0.6rem 0.8rem;
  background: transparent;
  border: none;
  color: ${glassTheme.colors.text.primary};
  text-align: left;
  font-size: 0.8rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all ${glassTheme.transition.base};
  outline: none;

  &:hover {
    background: rgba(96, 165, 250, 0.15);
  }

  &:focus {
    outline: none;
    background: rgba(96, 165, 250, 0.15);
  }

  &:not(:last-child) {
    border-bottom: 1px solid ${glassTheme.colors.border};
  }
`;

// Learning Activities Dropdown content (styled for use in portal)
const LearningActivitiesDropdownHeader = styled.div`
  padding: 0.5rem 0.6rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.muted};
  border-bottom: 1px solid ${glassTheme.colors.border};
  background: rgba(0, 0, 0, 0.2);
`;

const LearningActivitiesList = styled.div`
  padding: 0.25rem;
`;

const LearningActivityItem = styled.div<{ $isDragging?: boolean; $isDropTarget?: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.4rem 0.5rem;
  background: ${props => props.$isDragging ? 'rgba(96, 165, 250, 0.4)' : 'transparent'};
  border-radius: 6px;
  cursor: grab;
  font-size: 0.75rem;
  color: ${glassTheme.colors.text.primary};
  transition: all ${glassTheme.transition.base};
  border: ${props => props.$isDropTarget ? '2px dashed #60a5fa' : '2px solid transparent'};

  &:hover {
    background: rgba(96, 165, 250, 0.15);
  }

  &:active {
    cursor: grabbing;
  }
`;

const ActivityDragHandle = styled.span`
  cursor: grab;
  color: ${glassTheme.colors.text.muted};
  font-size: 0.65rem;
  user-select: none;

  &:hover {
    color: ${glassTheme.colors.text.primary};
  }
`;

const ActivityRemoveBtn = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 0 2px;
  font-size: 0.65rem;
  margin-left: auto;
  opacity: 0.6;
  transition: opacity ${glassTheme.transition.base};

  &:hover {
    opacity: 1;
  }
`;

// Toggle button container for learning activities dropdown
const ActivitiesToggleContainer = styled.div.attrs({ className: 'activities-toggle-container' })`
  position: relative;
  display: inline-flex;
`;

// Toggle button for learning activities
const ActivitiesToggleBtn = styled.button`
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.3);
  color: #22c55e;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 0.7rem;
  transition: all ${glassTheme.transition.base};
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(34, 197, 94, 0.25);
  }

  &:focus {
    outline: none;
  }

  &:active {
    background: rgba(34, 197, 94, 0.3);
  }
`;

const AddSectionBtn = styled.button`
  width: 100%;
  padding: 0.6rem;
  margin-top: 0.5rem;
  background: transparent;
  border: 1px dashed ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.muted};
  font-size: 0.8rem;
  cursor: pointer;
  transition: all ${glassTheme.transition.base};

  &:hover {
    background: rgba(96, 165, 250, 0.1);
    border-color: ${glassTheme.colors.accent.blue};
    color: ${glassTheme.colors.accent.blue};
  }
`;

// Right Panel - Topics Library
const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  overflow: hidden;
`;

const TopicsLibraryHeader = styled.div`
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${glassTheme.colors.border};
  background: rgba(0, 0, 0, 0.2);
`;

const TopicsLibraryTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 0.75rem 0;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.6rem 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.blue};
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const TopicsLibraryContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(96, 165, 250, 0.3);
    border-radius: 3px;

    &:hover {
      background: rgba(96, 165, 250, 0.5);
    }
  }
`;

const TopicCard = styled.div<{ $isDragging?: boolean; $inCourse: boolean }>`
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  background: ${props => props.$inCourse ? 'rgba(34, 197, 94, 0.1)' : 'rgba(0, 0, 0, 0.2)'};
  border: 1px solid ${props => props.$inCourse ? 'rgba(34, 197, 94, 0.3)' : glassTheme.colors.border};
  border-radius: 8px;
  cursor: grab;
  transition: all ${glassTheme.transition.base};
  opacity: ${props => props.$inCourse ? 0.6 : 1};

  &:hover {
    border-color: ${glassTheme.colors.borderHover};
    background: rgba(0, 0, 0, 0.3);
    transform: translateX(2px);
  }

  &:active {
    cursor: grabbing;
  }
`;

const TopicCardTitle = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const TopicCardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  color: ${glassTheme.colors.text.muted};
`;

const Badge = styled.span<{ $color?: 'blue' | 'green' }>`
  padding: 0.15rem 0.5rem;
  border-radius: 4px;
  font-size: 0.65rem;
  font-weight: 600;
  background: ${props => props.$color === 'green' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(96, 165, 250, 0.2)'};
  color: ${props => props.$color === 'green' ? '#22c55e' : glassTheme.colors.accent.blue};
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  color: ${glassTheme.colors.text.muted};
`;

// Modal styles for editing course info
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: ${glassTheme.glass.container};
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.lg};
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${glassTheme.colors.text.primary};
  margin: 0 0 1.5rem 0;
`;

const ModalField = styled.div`
  margin-bottom: 1.25rem;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${glassTheme.colors.text.secondary};
  margin-bottom: 0.5rem;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  transition: all ${glassTheme.transition.base};

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.blue};
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: 8px;
  color: ${glassTheme.colors.text.primary};
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all ${glassTheme.transition.base};

  &:focus {
    outline: none;
    border-color: ${glassTheme.colors.accent.blue};
    box-shadow: 0 0 0 3px rgba(96, 165, 250, 0.2);
  }

  &::placeholder {
    color: ${glassTheme.colors.text.muted};
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

// Stats Panel
const StatsPanel = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.2);
  border-top: 1px solid ${glassTheme.colors.border};
`;

const StatItem = styled.div`
  flex: 1;
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${glassTheme.colors.text.primary};
`;

const StatLabel = styled.div`
  font-size: 0.7rem;
  color: ${glassTheme.colors.text.muted};
  margin-top: 0.25rem;
`;

type DragItem = {
  type: 'topic';
  topic: Topic;
  source: 'browser' | 'chapter' | 'section';
  chapterId?: string;
  sectionId?: string;
  topicIndex?: number;
};

export const CourseEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId?: string }>();
  const { user } = useAuthStore();
  const isNew = courseId === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [course, setCourse] = useState<Course | null>(null);
  const courseRef = useRef<Course | null>(null); // Ref to track latest course for auto-save
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isEditing, setIsEditing] = useState(isNew);
  const [isPublished, setIsPublished] = useState(false);

  // Topics browser state
  const [allTopics, setAllTopics] = useState<Topic[]>([]);
  const [topicsSearch, setTopicsSearch] = useState('');
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [expandedChapters, setExpandedChapters] = useState<Set<string>>(new Set());

  // Drag and drop state
  const [draggedItem, setDraggedItem] = useState<DragItem | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    type: 'chapter' | 'section';
    chapterId: string;
    sectionId?: string;
    insertIndex?: number;
  } | null>(null);

  // Auto-save state
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveTimeout, setSaveTimeout] = useState<NodeJS.Timeout | null>(null);
  const lastSaveTimeRef = useRef<number>(0); // Track last save time to prevent rapid duplicate saves

  // Helper to save with debounce protection
  const saveCourseWithDebounce = async (chapters: any[]) => {
    if (!course) return;

    const now = Date.now();
    if (now - lastSaveTimeRef.current < 500) {
      console.log('Skipping duplicate save request (too soon)');
      return;
    }
    lastSaveTimeRef.current = now;

    try {
      setSaving(true);
      await teacherApi.saveDraft(course._id, {
        title,
        description,
        chapters
      });
      setLastSaved(new Date());
      console.log('Course saved to database');
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // Course info edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  // Chapter modal state
  const [showChapterModal, setShowChapterModal] = useState(false);
  const [chapterModalMode, setChapterModalMode] = useState<'add' | 'edit'>('add');
  const [chapterModalTitle, setChapterModalTitle] = useState('');
  const [chapterModalDescription, setChapterModalDescription] = useState('');
  const [editingChapterId, setEditingChapterId] = useState<string | null>(null);

  // Section modal state
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [sectionModalMode, setSectionModalMode] = useState<'add' | 'edit'>('add');
  const [sectionModalTitle, setSectionModalTitle] = useState('');
  const [editingSectionChapterId, setEditingSectionChapterId] = useState<string | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  // Activity dropdown state
  const [openActivityDropdown, setOpenActivityDropdown] = useState<string | null>(null);
  const [addActivityDropdownAnchor, setAddActivityDropdownAnchor] = useState<HTMLDivElement | null>(null);

  // Learning activities panel state - tracks which topics have their activities panel expanded
  const [expandedActivitiesPanels, setExpandedActivitiesPanels] = useState<Set<string>>(new Set());

  // Activity drag state for reordering
  const [draggedActivity, setDraggedActivity] = useState<{ topicId: string; activityIndex: number; activityType: string } | null>(null);

  // Store the current dropdown anchor element
  const [activitiesDropdownAnchor, setActivitiesDropdownAnchor] = useState<HTMLDivElement | null>(null);

  // Track drop target index for activity reordering
  const [activityDropIndex, setActivityDropIndex] = useState<number | null>(null);

  // Video modal state
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoModalTitle, setVideoModalTitle] = useState('');
  const [videoModalUrl, setVideoModalUrl] = useState('');
  const [videoModalContext, setVideoModalContext] = useState<{ topicId: string; chapterId: string; sectionId?: string } | null>(null);
  const [videoPreviewMode, setVideoPreviewMode] = useState(false); // true = showing preview

  // Reading modal state
  const [showReadingModal, setShowReadingModal] = useState(false);
  const [readingModalContext, setReadingModalContext] = useState<{ topicId: string; chapterId: string; sectionId?: string } | null>(null);
  const [readingUseTopicContent, setReadingUseTopicContent] = useState(false);
  const [readingModalContent, setReadingModalContent] = useState('');
  const [readingModalLink, setReadingModalLink] = useState('');

  // Survey modal state
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyModalMode, setSurveyModalMode] = useState<'select' | 'input'>('select');
  const [surveyModalContext, setSurveyModalContext] = useState<{ topicId: string; chapterId: string; sectionId?: string } | null>(null);
  const [availableSurveys, setAvailableSurveys] = useState<any[]>([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState('');
  const [surveyInputTitle, setSurveyInputTitle] = useState('');
  const [surveyInputLink, setSurveyInputLink] = useState('');
  const [surveyInputDescription, setSurveyInputDescription] = useState('');
  const [surveyInputScoring, setSurveyInputScoring] = useState('');

  useEffect(() => {
    if (user?.teacherInfo?.approvalStatus !== 'approved') {
      toast.error('您的教师账号尚未通过审核');
      navigate('/teacher/dashboard');
      return;
    }

    // Reset state when courseId changes or becomes undefined
    if (!courseId) {
      setCourse(null);
      setTitle('');
      setDescription('');
      setIsPublished(false);
      setIsEditing(false);
      setLoading(false);
      return;
    }

    if (!isNew) {
      loadCourse(courseId);
    }

    loadTopics();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isNew, courseId]);

  const loadCourse = async (id: string, preserveScroll: boolean = false) => {
    // Find the currently visible topic element before loading (both in course and library)
    let visibleTopicId: string | null = null;
    let visibleLibraryTopicId: string | null = null;
    if (preserveScroll) {
      // Find visible topic in course content
      const topicElements = document.querySelectorAll('[data-topic-id]');
      const viewportCenter = window.innerHeight / 2 + window.scrollY;

      // Find the topic closest to viewport center
      let closestTopic: Element | undefined;
      let minDistance = Infinity;

      topicElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestTopic = el;
        }
      });

      if (closestTopic) {
        visibleTopicId = closestTopic!.getAttribute('data-topic-id');
      }

      // Find visible topic in library
      const libraryTopicElements = document.querySelectorAll('[data-library-topic-id]');
      let closestLibraryTopic: Element | undefined;
      let minLibraryDistance = Infinity;

      libraryTopicElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < minLibraryDistance) {
          minLibraryDistance = distance;
          closestLibraryTopic = el;
        }
      });

      if (closestLibraryTopic) {
        visibleLibraryTopicId = closestLibraryTopic!.getAttribute('data-library-topic-id');
      }
    }
    try {
      setLoading(true);
      const data = await teacherApi.getCourseById(id);
      setCourse(data);
      setTitle(data.title);
      setDescription(data.description);
      setIsPublished(data.isPublished);

      // Expand all chapters by default (only if not preserving scroll - to avoid resetting expanded state)
      if (!preserveScroll) {
        const chapterIds = data.chapters.map(c => c._id);
        setExpandedChapters(new Set(chapterIds));
      }
    } catch (error) {
      console.error('Failed to load course:', error);
      toast.error('加载课程失败');
      navigate('/teacher/courses');
    } finally {
      setLoading(false);
      // Scroll to the previously visible topic (in course content)
      if (preserveScroll && visibleTopicId) {
        setTimeout(() => {
          const targetElement = document.querySelector(`[data-topic-id="${visibleTopicId}"]`);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
          }
        }, 100);
      }
      // Scroll to the previously visible library topic
      if (preserveScroll && visibleLibraryTopicId) {
        setTimeout(() => {
          const targetElement = document.querySelector(`[data-library-topic-id="${visibleLibraryTopicId}"]`);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
          }
        }, 100);
      }
    }
  };

  // Keep courseRef in sync with course state
  useEffect(() => {
    courseRef.current = course;
  }, [course]);

  const loadTopics = async (preserveScroll: boolean = false) => {
    // Find the currently visible library topic element before loading
    let visibleLibraryTopicId: string | null = null;
    if (preserveScroll) {
      const topicElements = document.querySelectorAll('[data-library-topic-id]');
      const viewportCenter = window.innerHeight / 2 + window.scrollY;

      // Find the library topic closest to viewport center
      let closestTopic: Element | undefined;
      let minDistance = Infinity;

      topicElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        const distance = Math.abs(rect.top + rect.height / 2 - viewportCenter);
        if (distance < minDistance) {
          minDistance = distance;
          closestTopic = el;
        }
      });

      if (closestTopic) {
        visibleLibraryTopicId = closestTopic!.getAttribute('data-library-topic-id');
      }
    }

    try {
      setLoadingTopics(true);
      console.log('Fetching topics from API...');
      // Fetch all published topics with a large limit to get all available topics
      const response = await topicsApi.getAllTopics({ published: true, limit: 1000 });
      console.log('API Response:', response);
      console.log('Topics array:', response.topics);
      console.log('Loaded topics:', response.topics.length, 'topics');
      console.log('First topic (if any):', response.topics[0]);

      if (response.topics && response.topics.length > 0) {
        setAllTopics(response.topics);
      } else {
        console.warn('No topics returned from API - topics might not be published or database is empty');
        // Use static FLAT_TOPICS as fallback for testing/development
        console.log('Using static FLAT_TOPICS data as fallback');
        const { FLAT_TOPICS } = await import('../../data/excelTopics');
        const staticTopics: Topic[] = FLAT_TOPICS.map(ft => ({
          _id: ft.id, // Use id as _id for static topics
          id: ft.id,
          title: ft.title,
          slug: ft.code,
          description: ft.description,
          category: {
            _id: ft.mainCategoryCode,
            name: ft.mainCategoryName,
            slug: ft.mainCategoryCode,
          },
          published: true,
          order: 0,
        }));
        console.log('Loaded', staticTopics.length, 'static topics as fallback');
        setAllTopics(staticTopics);
        toast('使用静态知识元数据（数据库中可能没有已发布的话题）');
      }
    } catch (error: any) {
      console.error('Failed to load topics:', error);
      console.error('Error response:', error.response);
      toast.error('加载知识元失败: ' + (error.response?.data?.message || error.message));
      // Fallback to static topics on error
      try {
        console.log('Attempting to use static FLAT_TOPICS as fallback');
        const { FLAT_TOPICS } = await import('../../data/excelTopics');
        const staticTopics: Topic[] = FLAT_TOPICS.map(ft => ({
          _id: ft.id,
          id: ft.id,
          title: ft.title,
          slug: ft.code,
          description: ft.description,
          category: {
            _id: ft.mainCategoryCode,
            name: ft.mainCategoryName,
            slug: ft.mainCategoryCode,
          },
          published: true,
          order: 0,
        }));
        setAllTopics(staticTopics);
        toast('使用静态知识元数据');
      } catch (fallbackError) {
        console.error('Failed to load static topics:', fallbackError);
      }
    } finally {
      setLoadingTopics(false);
      // Scroll to the previously visible library topic
      if (preserveScroll && visibleLibraryTopicId) {
        setTimeout(() => {
          const targetElement = document.querySelector(`[data-library-topic-id="${visibleLibraryTopicId}"]`);
          if (targetElement) {
            targetElement.scrollIntoView({ behavior: 'auto', block: 'center' });
          }
        }, 100);
      }
    }
  };

  // Auto-save draft - triggers when title or description changes
  // Note: Chapters are saved manually after drag-and-drop operations
  useEffect(() => {
    if (!course || isNew) return;

    // Clear existing timeout
    if (saveTimeout) clearTimeout(saveTimeout);

    // Debounced auto-save for title/description changes only
    const timeout = setTimeout(async () => {
      // Use courseRef to get the latest course data (avoids stale closure)
      const currentCourse = courseRef.current;
      if (!currentCourse) return;

      console.log('Auto-save triggered (title/description change)');
      await saveCourseWithDebounce(currentCourse.chapters);
    }, 1500); // 1.5 second debounce

    setSaveTimeout(timeout);

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description]); // Removed course?.chapters - chapters are saved manually

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error('请输入课程标题');
      return;
    }

    if (!description.trim()) {
      toast.error('请输入课程描述');
      return;
    }

    try {
      setSaving(true);

      if (isNew) {
        const newCourse = await teacherApi.createCourse({ title, description });
        toast.success('课程创建成功');
        navigate(`/teacher/courses/${newCourse._id}`);
      } else {
        await teacherApi.updateCourse(courseId!, { title, description, isPublished });
        toast.success('课程保存成功');
        loadCourse(courseId!);
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error('Failed to save course:', error);
      toast.error(error.response?.data?.message || '保存课程失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!course) return;
    const confirmed = window.confirm(`确定要删除课程"${course.title}"吗？此操作不可撤销。`);
    if (!confirmed) return;

    try {
      await teacherApi.deleteCourse(course._id);
      toast.success('课程删除成功');
      navigate('/teacher/courses');
    } catch (error: any) {
      console.error('Failed to delete course:', error);
      toast.error(error.response?.data?.message || '删除课程失败');
    }
  };

  const copyCourseCode = () => {
    if (course?.courseCode) {
      navigator.clipboard.writeText(course.courseCode);
      toast.success('课程代码已复制到剪贴板');
    }
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
  };

  const toggleChapterExpanded = (chapterId: string) => {
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  // Chapter management
  const handleAddChapter = () => {
    setChapterModalMode('add');
    setChapterModalTitle('');
    setChapterModalDescription('');
    setEditingChapterId(null);
    setShowChapterModal(true);
  };

  const handleEditChapter = (chapterId: string) => {
    if (!course) return;
    const chapter = course.chapters.find(c => c._id === chapterId);
    if (!chapter) return;

    setChapterModalMode('edit');
    setChapterModalTitle(chapter.title);
    setChapterModalDescription(chapter.description || '');
    setEditingChapterId(chapterId);
    setShowChapterModal(true);
  };

  const saveChapter = async () => {
    if (!course || !chapterModalTitle.trim()) {
      toast.error('请输入章节标题');
      return;
    }

    try {
      if (chapterModalMode === 'add') {
        await teacherApi.addChapter(course._id, {
          title: chapterModalTitle.trim(),
          description: chapterModalDescription.trim(),
          order: course.chapters.length
        });
        toast.success('章节添加成功');
      } else if (editingChapterId) {
        await teacherApi.updateChapter(course._id, editingChapterId, {
          title: chapterModalTitle.trim(),
          description: chapterModalDescription.trim()
        });
        toast.success('章节更新成功');
      }
      await loadCourse(course._id, true);
      setShowChapterModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || '操作失败');
    }
  };

  const closeChapterModal = () => {
    setShowChapterModal(false);
    setChapterModalTitle('');
    setChapterModalDescription('');
    setEditingChapterId(null);
  };

  const handleDeleteChapter = async (chapterId: string) => {
    if (!course) return;
    const chapter = course.chapters.find(c => c._id === chapterId);
    if (!chapter) return;

    const confirmed = window.confirm(`确定要删除章节"${chapter.title}"吗？此操作不可撤销。`);
    if (!confirmed) return;

    try {
      await teacherApi.deleteChapter(course._id, chapterId);
      await loadCourse(course._id, true);
      toast.success('章节删除成功');
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除章节失败');
    }
  };

  // Section management
  const handleAddSection = (chapterId: string) => {
    setSectionModalMode('add');
    setSectionModalTitle('');
    setEditingSectionChapterId(chapterId);
    setEditingSectionId(null);
    setShowSectionModal(true);
  };

  const handleEditSection = (chapterId: string, sectionId: string) => {
    if (!course) return;

    const chapter = course.chapters.find(c => c._id === chapterId);
    if (!chapter) return;

    const section = chapter.sections?.find(s => s._id === sectionId);
    if (!section) return;

    setSectionModalMode('edit');
    setSectionModalTitle(section.title);
    setEditingSectionChapterId(chapterId);
    setEditingSectionId(sectionId);
    setShowSectionModal(true);
  };

  const saveSection = async () => {
    if (!course || !editingSectionChapterId || !sectionModalTitle.trim()) {
      toast.error('请输入小节标题');
      return;
    }

    try {
      if (sectionModalMode === 'add') {
        await teacherApi.addSection(course._id, editingSectionChapterId, {
          title: sectionModalTitle.trim(),
          order: 0
        });
        toast.success('小节添加成功');
      } else if (editingSectionId) {
        await teacherApi.updateSection(course._id, editingSectionChapterId, editingSectionId, {
          title: sectionModalTitle.trim()
        });
        toast.success('小节更新成功');
      }
      await loadCourse(course._id, true);
      setShowSectionModal(false);
    } catch (error: any) {
      toast.error(error.response?.data?.message || error.message || '操作失败');
    }
  };

  const closeSectionModal = () => {
    setShowSectionModal(false);
    setSectionModalTitle('');
    setEditingSectionChapterId(null);
    setEditingSectionId(null);
  };

  const handleDeleteSection = async (chapterId: string, sectionId: string) => {
    if (!course) return;

    const chapter = course.chapters.find(c => c._id === chapterId);
    if (!chapter) return;

    const section = chapter.sections?.find(s => s._id === sectionId);
    if (!section) return;

    const confirmed = window.confirm(`确定要删除小节"${section.title}"吗？此操作不可撤销。`);
    if (!confirmed) return;

    try {
      await teacherApi.deleteSection(course._id, chapterId, sectionId);
      await loadCourse(course._id, true);
      toast.success('小节删除成功');
    } catch (error: any) {
      toast.error(error.response?.data?.message || '删除小节失败');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (topic: Topic, source: DragItem['source'], chapterId?: string, sectionId?: string, topicIndex?: number) => {
    setDraggedItem({ type: 'topic', topic, source, chapterId, sectionId, topicIndex });
  };

  const handleDragOver = (e: React.DragEvent, target: { type: 'chapter' | 'section'; chapterId: string; sectionId?: string; insertIndex?: number }) => {
    e.preventDefault();
    setDropTarget(target);
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDrop = async (e: React.DragEvent, target: { type: 'chapter' | 'section'; chapterId: string; sectionId?: string; insertIndex?: number }) => {
    e.preventDefault();
    setDropTarget(null);

    if (!draggedItem || !course) return;

    try {
      // Check if reordering within the same container
      const isSameContainer =
        draggedItem.source === target.type &&
        draggedItem.chapterId === target.chapterId &&
        draggedItem.sectionId === target.sectionId;

      if (isSameContainer && typeof draggedItem.topicIndex === 'number' && typeof target.insertIndex === 'number') {
        // Reorder within the same container
        const chapter = course.chapters.find(c => c._id === target.chapterId);
        if (!chapter) return;

        let topicIds: string[] = [];
        if (target.type === 'section' && target.sectionId) {
          const section = chapter.sections?.find(s => s._id === target.sectionId);
          if (!section) return;
          topicIds = [...section.topics];
        } else {
          topicIds = [...chapter.topics];
        }

        // Remove from old position and insert at new position
        const [removed] = topicIds.splice(draggedItem.topicIndex, 1);
        topicIds.splice(target.insertIndex, 0, removed);

        // Call reorder API
        if (target.type === 'section' && target.sectionId) {
          await teacherApi.reorderTopicsInSection(course._id, target.chapterId, target.sectionId, topicIds);
        } else {
          await teacherApi.reorderTopicsInChapter(course._id, target.chapterId, topicIds);
        }

        await loadCourse(course._id, true);
        toast.success('知识元顺序已更新');
      } else {
        // Moving to a different container or from browser
        // Remove from source if it's already in the course
        if (draggedItem.source === 'chapter' && draggedItem.chapterId) {
          await teacherApi.removeTopicFromChapter(course._id, draggedItem.chapterId, draggedItem.topic._id);
        } else if (draggedItem.source === 'section' && draggedItem.chapterId && draggedItem.sectionId) {
          await teacherApi.removeTopicFromSection(course._id, draggedItem.chapterId, draggedItem.sectionId, draggedItem.topic._id);
        }

        // Add to target
        if (target.type === 'chapter') {
          await teacherApi.addTopicsToChapter(course._id, target.chapterId, [draggedItem.topic._id]);
        } else if (target.type === 'section' && target.sectionId) {
          await teacherApi.addTopicsToSection(course._id, target.chapterId, target.sectionId, [draggedItem.topic._id]);
        }

        await loadCourse(course._id, true);
        toast.success('知识元已添加');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || '操作失败');
    }

    setDraggedItem(null);
  };

  const handleRemoveTopic = async (topicId: string, chapterId: string, sectionId?: string) => {
    if (!course) return;

    try {
      if (sectionId) {
        await teacherApi.removeTopicFromSection(course._id, chapterId, sectionId, topicId);
      } else {
        await teacherApi.removeTopicFromChapter(course._id, chapterId, topicId);
      }
      await loadCourse(course._id, true);
      toast.success('知识元已移除');
    } catch (error: any) {
      toast.error(error.response?.data?.message || '移除失败');
    }
  };

  // Activity dropdown handlers
  const toggleActivityDropdown = (topicId: string, anchorElement?: HTMLDivElement | null) => {
    if (openActivityDropdown === topicId) {
      setOpenActivityDropdown(null);
      setAddActivityDropdownAnchor(null);
    } else {
      setOpenActivityDropdown(topicId);
      setAddActivityDropdownAnchor(anchorElement || null);
    }
  };

  // Toggle learning activities panel visibility
  const toggleActivitiesPanel = (topicId: string, anchorElement?: HTMLDivElement | null) => {
    setExpandedActivitiesPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(topicId)) {
        newSet.delete(topicId);
        setActivitiesDropdownAnchor(null);
      } else {
        // Close any other open dropdowns first
        newSet.clear();
        newSet.add(topicId);
        setActivitiesDropdownAnchor(anchorElement || null);
      }
      return newSet;
    });
  };

  // Helper function to extract topic ID as string (handles ObjectId objects and strings)
  const getTopicIdString = (topicRef: any): string => {
    if (!topicRef) return '';
    if (typeof topicRef === 'string') return topicRef;
    const tid = topicRef.topicId?._id || topicRef.topicId?.toString() || topicRef.topicId || topicRef._id || '';
    return typeof tid === 'object' && tid !== null ? tid.toString() : String(tid || '');
  };

  // Get activities list for a topic - returns array of { id, type, title, label, icon } in sorted order
  // New structure: activities = [{ _id, name: 'reading', title: '...', value: true, sortingIndex: 0 }, ...]
  const getTopicActivities = useCallback((topicRef: any): { id: string; type: string; title: string; label: string; icon: string }[] => {
    if (!topicRef?.activities) return [];

    const activityConfigs: Record<string, { label: string; icon: string }> = {
      reading: { label: '阅读', icon: '📖' },
      video: { label: '视频', icon: '🎬' },
      exercise: { label: '练习', icon: '✏️' },
      review: { label: '复习', icon: '🔄' },
      survey: { label: '问卷测量', icon: '📊' },
      selfExplanation: { label: 'Self Explanation', icon: '💭' },
    };

    // Check if activities is an array (new format) or object (old format)
    if (Array.isArray(topicRef.activities)) {
      // New array format - activities is already an array
      // Sort by sortingIndex
      const sortedActivities = [...topicRef.activities]
        .filter((act: any) => act.sortingIndex >= 0)
        .sort((a: any, b: any) => a.sortingIndex - b.sortingIndex);

      return sortedActivities.map((act: any) => ({
        id: act._id,
        type: act.name,
        title: act.title || '',
        label: activityConfigs[act.name]?.label || act.name,
        icon: activityConfigs[act.name]?.icon || '📌',
      }));
    }

    // Old object format - convert to array (for backward compatibility)
    const acts = topicRef.activities;
    const activeActivities: { id: string; type: string; title: string; label: string; icon: string; sortingIndex: number }[] = [];

    Object.keys(activityConfigs).forEach(key => {
      const activityData = acts[key];
      if (activityData && typeof activityData === 'object' && 'value' in activityData) {
        const val = activityData.value;
        const hasValue = (typeof val === 'boolean' && val === true) ||
          (typeof val === 'string' && val.trim() !== '') ||
          (Array.isArray(val) && val.length > 0);

        if (hasValue) {
          activeActivities.push({
            id: key, // Use key as id for old format
            type: key,
            title: '',
            label: activityConfigs[key].label,
            icon: activityConfigs[key].icon,
            sortingIndex: typeof activityData.sortingIndex === 'number' ? activityData.sortingIndex : -1
          });
        }
      }
    });

    // Sort by sortingIndex
    const sorted = activeActivities.filter(a => a.sortingIndex >= 0)
      .sort((a, b) => a.sortingIndex - b.sortingIndex);
    const unsorted = activeActivities.filter(a => a.sortingIndex < 0);

    return [...sorted, ...unsorted];
  }, []);

  // Helper function to find a topic reference in chapters by topicId
  const findTopicRef = (chapters: any[], topicId: string, chapterId: string, sectionId?: string): { topicRef: any; chapter: any } | null => {
    console.log('findTopicRef called with:', { topicId, chapterId, sectionId });
    const chapter = chapters.find(c => c._id === chapterId);
    if (!chapter) {
      console.log('Chapter not found for chapterId:', chapterId);
      return null;
    }

    let topicRef: any = null;

    // Helper to compare topic IDs (handles ObjectId objects and strings)
    const isMatchingTopic = (t: any, targetTopicId: string) => {
      const tid = t?.topicId?._id || t?.topicId?.toString() || t?.topicId || (typeof t === 'string' ? t : t?._id);
      const tidStr = typeof tid === 'object' && tid !== null ? tid.toString() : String(tid || '');
      console.log('Comparing tidStr:', tidStr, 'with targetTopicId:', targetTopicId, 'match:', tidStr === targetTopicId);
      return tidStr === targetTopicId || tidStr === String(targetTopicId);
    };

    if (sectionId) {
      const section = chapter.sections?.find((s: any) => s._id === sectionId);
      if (section) {
        console.log('Searching in section, topics count:', section.topics?.length);
        topicRef = section.topics.find((t: any) => isMatchingTopic(t, topicId));
      }
    } else {
      console.log('Searching in chapter directly, topics count:', chapter.topics?.length);
      topicRef = chapter.topics?.find((t: any) => isMatchingTopic(t, topicId));
    }

    console.log('findTopicRef result:', topicRef ? 'found' : 'not found');
    return topicRef ? { topicRef, chapter } : null;
  };

  const handleAddActivity = async (activityType: string, topicId: string, chapterId: string, sectionId?: string, title?: string) => {
    setOpenActivityDropdown(null);

    if (!course) return;

    const activityNames: Record<string, string> = {
      'reading': '阅读',
      'video': '视频',
      'selfExplanation': 'Self Explanation',
      'exercise': '练习',
      'review': '复习',
      'survey': '问卷测量'
    };

    try {
      // Find the chapter and update the topic's activities
      const updatedChapters = [...course.chapters];
      const result = findTopicRef(updatedChapters, topicId, chapterId, sectionId);
      if (!result) return;

      const { topicRef } = result;

      if (topicRef) {
        // Initialize activities array if not present (new structure)
        if (!topicRef.activities) {
          topicRef.activities = [];
        }

        // Convert old object format to array if needed
        if (!Array.isArray(topicRef.activities)) {
          const oldActivities = topicRef.activities;
          topicRef.activities = [];
          // Migrate old format to new array format
          Object.keys(oldActivities).forEach(name => {
            const act = oldActivities[name];
            if (act && typeof act === 'object' && 'value' in act) {
              const val = act.value;
              const hasValue = (typeof val === 'boolean' && val === true) ||
                (typeof val === 'string' && val.trim() !== '') ||
                (Array.isArray(val) && val.length > 0);
              if (hasValue) {
                topicRef.activities.push({
                  name,
                  value: act.value,
                  sortingIndex: typeof act.sortingIndex === 'number' ? act.sortingIndex : -1
                });
              }
            }
          });
        }

        // Get current max sorting index
        const maxSortingIndex = topicRef.activities.reduce((max: number, act: any) => {
          return typeof act.sortingIndex === 'number' && act.sortingIndex > max ? act.sortingIndex : max;
        }, -1);

        const newSortingIndex = maxSortingIndex + 1;

        // Get default value for activity type
        const getDefaultValue = (type: string): any => {
          switch (type) {
            case 'reading': return true;
            case 'video': return 'https://';
            case 'selfExplanation': return true;
            case 'exercise': return [];
            case 'review': return [new Date()];
            case 'survey': return 'pending';
            default: return null;
          }
        };

        // Add new activity to array
        topicRef.activities.push({
          name: activityType,
          title: title || '',
          value: getDefaultValue(activityType),
          sortingIndex: newSortingIndex
        });

        // Update course state
        const updatedCourse = { ...course, chapters: updatedChapters };
        setCourse(updatedCourse);
        courseRef.current = updatedCourse;

        // Save directly to database
        await saveCourseWithDebounce(updatedChapters);
        toast.success(`已添加${activityNames[activityType]}活动`);
      }
    } catch (error) {
      console.error('Failed to add activity:', error);
      toast.error('添加活动失败');
    }
  };

  // Remove activity from topic by activity _id (supports multiple activities of same type)
  const handleRemoveActivity = async (activityId: string, topicId: string, chapterId: string, sectionId?: string) => {
    if (!course) return;

    // Deep copy chapters to ensure React detects the state change
    const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
    const result = findTopicRef(updatedChapters, topicId, chapterId, sectionId);
    if (!result) return;

    const { topicRef } = result;

    if (topicRef?.activities && Array.isArray(topicRef.activities)) {
      // Find and remove the activity from the array by _id
      const activityIndex = topicRef.activities.findIndex((act: any) => act._id === activityId);
      if (activityIndex >= 0) {
        topicRef.activities.splice(activityIndex, 1);

        // Re-assign sortingIndex values to maintain order
        topicRef.activities
          .filter((act: any) => act.sortingIndex >= 0)
          .sort((a: any, b: any) => a.sortingIndex - b.sortingIndex)
          .forEach((act: any, idx: number) => {
            act.sortingIndex = idx;
          });

        const updatedCourse = { ...course, chapters: updatedChapters };
        setCourse(updatedCourse);
        courseRef.current = updatedCourse;

        // Save directly to database
        await saveCourseWithDebounce(updatedChapters);
        toast.success('活动已移除');
      }
    }
  };

  // Handle video modal submission
  const handleVideoModalSubmit = async () => {
    if (!videoModalContext) return;
    if (!videoModalTitle.trim()) {
      toast.error('请输入视频标题');
      return;
    }
    if (!videoModalUrl.trim()) {
      toast.error('请输入视频链接');
      return;
    }

    // If not in preview mode, switch to preview mode
    if (!videoPreviewMode) {
      setVideoPreviewMode(true);
      return;
    }

    // In preview mode, save to database
    setShowVideoModal(false);
    setVideoPreviewMode(false);

    // Add video activity with URL as value
    await handleAddActivity('video', videoModalContext.topicId, videoModalContext.chapterId, videoModalContext.sectionId, videoModalTitle);

    // Update the video activity with the URL
    if (!course) return;
    const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
    const result = findTopicRef(updatedChapters, videoModalContext.topicId, videoModalContext.chapterId, videoModalContext.sectionId);
    if (result && result.topicRef?.activities) {
      // Find the last video activity and update its link
      const videoActivities = result.topicRef.activities.filter((act: any) => act.name === 'video');
      if (videoActivities.length > 0) {
        const lastVideo = videoActivities[videoActivities.length - 1];
        lastVideo.link = videoModalUrl;
        lastVideo.value = videoModalUrl; // Keep for backwards compatibility
      }
      const updatedCourse = { ...course, chapters: updatedChapters };
      setCourse(updatedCourse);
      courseRef.current = updatedCourse;
      await saveCourseWithDebounce(updatedChapters);
      toast.success('视频已添加');
    }

    setVideoModalContext(null);
  };

  // Handle reading modal submission
  const handleReadingModalSubmit = async () => {
    if (!readingModalContext) return;

    // Determine the title and link based on checkbox state
    let title: string;
    let link: string;

    if (readingUseTopicContent) {
      // Use topic content - store topic ID with prefix for identification
      title = '阅读本知识元内容';
      link = `topic:${readingModalContext.topicId}`;
    } else {
      // Use custom link (required) and title (optional)
      if (!readingModalLink.trim()) {
        toast.error('请输入阅读链接');
        return;
      }
      title = readingModalContent.trim() || '阅读活动';
      link = readingModalLink.trim();
    }

    setShowReadingModal(false);

    // Add reading activity
    await handleAddActivity('reading', readingModalContext.topicId, readingModalContext.chapterId, readingModalContext.sectionId, title);

    // Update the reading activity with the link
    if (!course) return;
    const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
    const result = findTopicRef(updatedChapters, readingModalContext.topicId, readingModalContext.chapterId, readingModalContext.sectionId);
    if (result && result.topicRef?.activities) {
      const readingActivities = result.topicRef.activities.filter((act: any) => act.name === 'reading');
      if (readingActivities.length > 0) {
        const lastReading = readingActivities[readingActivities.length - 1];
        lastReading.link = link;
      }
      const updatedCourse = { ...course, chapters: updatedChapters };
      setCourse(updatedCourse);
      courseRef.current = updatedCourse;
      await saveCourseWithDebounce(updatedChapters);
      toast.success('阅读活动已添加');
    }

    // Reset state
    setReadingModalContext(null);
    setReadingUseTopicContent(false);
    setReadingModalContent('');
    setReadingModalLink('');
  };

  // Helper function to extract video embed URL from various platforms
  const getVideoEmbedUrl = (url: string): string | null => {
    if (!url) return null;

    // YouTube
    const youtubeMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }

    // Bilibili
    const biliMatch = url.match(/bilibili\.com\/video\/(BV[a-zA-Z0-9]+)/);
    if (biliMatch) {
      return `https://player.bilibili.com/player.html?bvid=${biliMatch[1]}&high_quality=1`;
    }

    // Bilibili av
    const biliAvMatch = url.match(/bilibili\.com\/video\/av(\d+)/);
    if (biliAvMatch) {
      return `https://player.bilibili.com/player.html?aid=${biliAvMatch[1]}&high_quality=1`;
    }

    // Youku
    const youkuMatch = url.match(/youku\.com\/.*id_([a-zA-Z0-9=]+)/);
    if (youkuMatch) {
      return `https://player.youku.com/embed/${youkuMatch[1]}`;
    }

    // Tencent Video
    const qqMatch = url.match(/v\.qq\.com\/.*\/([a-zA-Z0-9]+)\.html/);
    if (qqMatch) {
      return `https://v.qq.com/txp/iframe/player.html?vid=${qqMatch[1]}`;
    }

    // If already an embed URL or unknown format, return as is
    if (url.includes('embed') || url.includes('player')) {
      return url;
    }

    return null;
  };

  // Fetch available surveys from database
  const fetchSurveys = async () => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
      const response = await fetch(`${API_BASE_URL}/surveys`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setAvailableSurveys(data.surveys || []);
      console.log('Fetched surveys:', data.surveys);
    } catch (error) {
      console.error('Failed to fetch surveys:', error);
      toast.error('获取问卷列表失败，请确保服务器已重启');
    }
  };

  // Handle survey modal submission
  const handleSurveyModalSubmit = async () => {
    if (!surveyModalContext) return;

    if (surveyModalMode === 'select') {
      // Selected from database
      if (!selectedSurveyId) {
        toast.error('请选择一个问卷');
        return;
      }
      const selectedSurvey = availableSurveys.find(s => s._id === selectedSurveyId);
      if (!selectedSurvey) {
        toast.error('问卷未找到');
        return;
      }

      setShowSurveyModal(false);

      // Add survey activity
      await handleAddActivity('survey', surveyModalContext.topicId, surveyModalContext.chapterId, surveyModalContext.sectionId, selectedSurvey.title);

      // Update the survey activity with additional data
      if (!course) return;
      const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
      const result = findTopicRef(updatedChapters, surveyModalContext.topicId, surveyModalContext.chapterId, surveyModalContext.sectionId);
      if (result && result.topicRef?.activities) {
        const surveyActivities = result.topicRef.activities.filter((act: any) => act.name === 'survey');
        if (surveyActivities.length > 0) {
          const lastSurvey = surveyActivities[surveyActivities.length - 1];
          lastSurvey.value = {
            surveyId: selectedSurvey._id,
            title: selectedSurvey.title,
            description: selectedSurvey.description,
            scoring: selectedSurvey.scoring
          };
        }
        const updatedCourse = { ...course, chapters: updatedChapters };
        setCourse(updatedCourse);
        courseRef.current = updatedCourse;
        await saveCourseWithDebounce(updatedChapters);
        toast.success('问卷已添加');
      }
    } else {
      // Manual input mode
      if (!surveyInputTitle.trim()) {
        toast.error('请输入问卷标题');
        return;
      }
      if (!surveyInputLink.trim()) {
        toast.error('请输入问卷链接');
        return;
      }

      setShowSurveyModal(false);

      // Add survey activity
      await handleAddActivity('survey', surveyModalContext.topicId, surveyModalContext.chapterId, surveyModalContext.sectionId, surveyInputTitle);

      // Update the survey activity with additional data
      if (!course) return;
      const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
      const result = findTopicRef(updatedChapters, surveyModalContext.topicId, surveyModalContext.chapterId, surveyModalContext.sectionId);
      if (result && result.topicRef?.activities) {
        const surveyActivities = result.topicRef.activities.filter((act: any) => act.name === 'survey');
        if (surveyActivities.length > 0) {
          const lastSurvey = surveyActivities[surveyActivities.length - 1];
          lastSurvey.value = {
            link: surveyInputLink,
            title: surveyInputTitle,
            description: surveyInputDescription,
            scoring: surveyInputScoring
          };
        }
        const updatedCourse = { ...course, chapters: updatedChapters };
        setCourse(updatedCourse);
        courseRef.current = updatedCourse;
        await saveCourseWithDebounce(updatedChapters);
        toast.success('问卷已添加');
      }
    }

    setSurveyModalContext(null);
  };

  // Handle activity drag and drop reordering
  // New structure: activities is an array [{ name, value, sortingIndex }, ...]
  const handleActivityDrop = async (topicId: string, fromIndex: number, toIndex: number, chapterId: string, sectionId?: string) => {
    if (!course) return;

    console.log('handleActivityDrop called:', { topicId, fromIndex, toIndex, chapterId, sectionId });

    // Deep copy chapters to ensure React detects the state change
    const updatedChapters = JSON.parse(JSON.stringify(course.chapters));
    const result = findTopicRef(updatedChapters, topicId, chapterId, sectionId);

    if (!result) {
      console.log('Topic not found');
      return;
    }

    const { topicRef } = result;

    if (!topicRef?.activities || !Array.isArray(topicRef.activities) || fromIndex === toIndex) {
      console.log('Condition not met');
      return;
    }

    // Get activities sorted by sortingIndex (these are references to objects in topicRef.activities)
    const sortedActivities = topicRef.activities
      .filter((act: any) => act.sortingIndex >= 0)
      .sort((a: any, b: any) => a.sortingIndex - b.sortingIndex);

    console.log('Current sorted activities:', sortedActivities.map((a: any) => a.name).join(', '));

    if (fromIndex >= sortedActivities.length || toIndex >= sortedActivities.length) {
      console.log('Index out of bounds');
      return;
    }

    // Find the actual activity objects in topicRef.activities by _id
    const fromActivitySorted = sortedActivities[fromIndex];
    const toActivitySorted = sortedActivities[toIndex];

    // Find these activities in the original topicRef.activities array
    const fromActivity = topicRef.activities.find((act: any) => act._id === fromActivitySorted._id);
    const toActivity = topicRef.activities.find((act: any) => act._id === toActivitySorted._id);

    if (!fromActivity || !toActivity) {
      console.log('Activities not found in original array');
      return;
    }

    // Swap sortingIndex values in the original array
    const tempIndex = fromActivity.sortingIndex;
    fromActivity.sortingIndex = toActivity.sortingIndex;
    toActivity.sortingIndex = tempIndex;

    console.log('Reordered activities:', topicRef.activities
      .filter((act: any) => act.sortingIndex >= 0)
      .sort((a: any, b: any) => a.sortingIndex - b.sortingIndex)
      .map((a: any) => `${a.name}(${a.sortingIndex})`)
      .join(', '));

    // Update local state immediately (don't wait for save)
    const updatedCourse = { ...course, chapters: updatedChapters };
    setCourse(updatedCourse);
    courseRef.current = updatedCourse;

    // Save to database
    try {
      setSaving(true);
      await teacherApi.saveDraft(course._id, {
        title,
        description,
        chapters: updatedChapters
      });
      setLastSaved(new Date());
      console.log('Course saved to database');
      toast.success('活动顺序已更新');
    } catch (error) {
      console.error('Failed to save course:', error);
      toast.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenActivityDropdown(null);
      setAddActivityDropdownAnchor(null);
    };
    if (openActivityDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActivityDropdown]);

  // Update course status
  const handleUpdateStatus = async (status: 'draft' | 'created' | 'published') => {
    if (!course) return;

    if (status === 'published') {
      const confirmed = window.confirm('确定要发布课程吗？发布后学生可以使用课程代码加入课程。');
      if (!confirmed) return;
    }

    try {
      // Try to update both status and isPublished
      const updated = await teacherApi.updateCourse(course._id, {
        isPublished: status === 'published'
      });
      setCourse(updated);
      setIsPublished(updated.isPublished);
      toast.success(`课程状态已更新为${status === 'draft' ? '草稿' : status === 'created' ? '已创建' : '已发布'}`);
    } catch (error: any) {
      toast.error(error.response?.data?.message || '状态更新失败');
    }
  };

  // Course info edit handlers
  const openEditModal = () => {
    setEditTitle(title);
    setEditDescription(description);
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditTitle('');
    setEditDescription('');
  };

  const saveCourseInfo = async () => {
    if (!course) return;
    if (!editTitle.trim()) {
      toast.error('课程标题不能为空');
      return;
    }

    try {
      setSaving(true);
      const updated = await teacherApi.updateCourse(course._id, {
        title: editTitle.trim(),
        description: editDescription.trim()
      });
      setCourse(updated);
      setTitle(updated.title);
      setDescription(updated.description);
      setLastSaved(new Date());
      toast.success('课程信息已更新');
      closeEditModal();
    } catch (error: any) {
      toast.error(error.response?.data?.message || '更新失败');
    } finally {
      setSaving(false);
    }
  };

  // Filter topics for browser
  const filteredTopics = allTopics.filter(t =>
    t.title.toLowerCase().includes(topicsSearch.toLowerCase()) ||
    t.description.toLowerCase().includes(topicsSearch.toLowerCase())
  );

  // Get topics already in course
  const getCourseTopicIds = useCallback(() => {
    if (!course) return new Set<string>();

    const topicIds = new Set<string>();
    course.chapters.forEach(chapter => {
      chapter.topics?.forEach((topicRef: any) => {
        const id = getTopicIdString(topicRef);
        if (id) topicIds.add(id);
      });
      chapter.sections?.forEach(section => {
        section.topics?.forEach((topicRef: any) => {
          const id = getTopicIdString(topicRef);
          if (id) topicIds.add(id);
        });
      });
    });
    return topicIds;
  }, [course]);

  const courseTopicIds = getCourseTopicIds();

  if (loading) {
    return (
      <DashboardLayout title="加载中..." subtitle="">
        <LoadingSpinner>加载中...</LoadingSpinner>
      </DashboardLayout>
    );
  }

  const canEdit = isNew || isEditing;

  // Show basic form for new course creation
  if (isNew) {
    return (
      <DashboardLayout title="创建新课程" subtitle="创建一个新课程">
        <form onSubmit={handleSubmit} style={{ maxWidth: '600px', margin: '0 auto' }}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: glassTheme.colors.text.primary }}>
              课程标题 *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="例如：统计学基础"
              required
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem'
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: glassTheme.colors.text.primary }}>
              课程描述 *
            </label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="描述课程的主要内容、学习目标等..."
              required
              rows={4}
              style={{
                width: '100%',
                padding: '0.75rem',
                background: 'rgba(0, 0, 0, 0.5)',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                fontSize: '1rem',
                resize: 'vertical'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/teacher/courses');
              }}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              返回
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{
                padding: '0.75rem 1.5rem',
                background: glassTheme.gradients.cyber,
                border: '1px solid #333',
                borderRadius: '8px',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
                opacity: saving ? 0.5 : 1
              }}
            >
              {saving ? '创建中...' : '创建'}
            </button>
          </div>
        </form>
      </DashboardLayout>
    );
  }

  // Main editing interface
  return (
    <DashboardLayout
      title={course?.title || '编辑课程'}
      subtitle={course?.courseCode ? `课程代码: ${course.courseCode}` : '编辑课程信息和设置'}
    >
      <PageContainer>
        {/* Left Panel - Course Structure */}
        <LeftPanel>
          {/* Course Header */}
          <CourseHeader>
            <CourseTitleRow>
              <CourseTitle>{title}</CourseTitle>
              <EditIconButton onClick={openEditModal} title="编辑课程信息">
                ✏️
              </EditIconButton>
              <StatusBadge $status={course?.status || 'draft'}>
                {course?.status === 'draft' ? '草稿' : course?.status === 'created' ? '已创建' : '已发布'}
              </StatusBadge>
            </CourseTitleRow>

            <CourseMeta>
              {course?.courseCode && (
                <>
                  <CourseCode onClick={copyCourseCode} title="点击复制">
                    🔑 {course.courseCode}
                  </CourseCode>
                  <span>•</span>
                </>
              )}
              <span>{course?.chapters.length || 0} 章节</span>
              <span>•</span>
              <span>{course?.totalTopics || 0} 知识元</span>
              {lastSaved && (
                <>
                  <span>•</span>
                  <span>上次保存: {lastSaved.toLocaleTimeString()}</span>
                </>
              )}
            </CourseMeta>

            <ActionButtons style={{ marginTop: '1rem' }}>
              <BackLink to="/teacher/courses">
                ← 返回
              </BackLink>
              {!isPublished && (
                <IconButton $variant="success" onClick={() => handleUpdateStatus('published')}>
                  🚀 发布
                </IconButton>
              )}
              {isPublished && (
                <IconButton $variant="secondary" onClick={() => handleUpdateStatus('created')}>
                  ← 取消发布
                </IconButton>
              )}
              <IconButton $variant="danger" onClick={handleDelete}>
                🗑️ 删除
              </IconButton>
            </ActionButtons>
          </CourseHeader>

          {/* Course Structure */}
          <StructurePanel>
            <StructureHeader>
              <StructureTitle>📚 课程结构</StructureTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                {saving && (
                  <span style={{ color: glassTheme.colors.text.muted, fontSize: '0.875rem' }}>
                    保存中...
                  </span>
                )}
                {lastSaved && !saving && (
                  <span style={{ color: glassTheme.colors.text.muted, fontSize: '0.875rem' }}>
                    已自动保存
                  </span>
                )}
                <button
                onClick={handleAddChapter}
                style={{
                  padding: '0.4rem 0.75rem',
                  background: 'rgba(96, 165, 250, 0.15)',
                  border: '1px solid rgba(96, 165, 250, 0.3)',
                  borderRadius: '6px',
                  color: '#60a5fa',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.4rem'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(96, 165, 250, 0.25)';
                  e.currentTarget.style.transform = 'translateY(-1px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(96, 165, 250, 0.15)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
              >
                ➕ 添加章节
              </button>
              </div>
            </StructureHeader>

            <StructureContent>
              {!course || course.chapters.length === 0 ? (
                <EmptyState>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📖</div>
                  <div style={{ marginBottom: '1rem' }}>还没有章节</div>
                  <IconButton onClick={handleAddChapter}>➕ 添加第一个章节</IconButton>
                </EmptyState>
              ) : (
                <ChapterList>
                  {course.chapters.map((chapter, idx) => (
                    <ChapterItem
                      key={chapter._id}
                      $isDragging={draggedItem?.chapterId === chapter._id && !draggedItem?.sectionId}
                      $isOver={dropTarget?.chapterId === chapter._id && !dropTarget?.sectionId}
                    >
                      <ChapterHeader onClick={() => toggleChapterExpanded(chapter._id)}>
                        <ChapterNumber>{idx + 1}</ChapterNumber>
                        <ChapterInfo>
                          <ChapterTitle>{chapter.title}</ChapterTitle>
                          <ChapterMeta>
                            <span>{chapter.sections?.length || 0} 小节</span>
                            <span>•</span>
                            <span>
                              {chapter.sections?.reduce((acc, s) => acc + s.topics.length, 0) || chapter.topics?.length || 0} 知识元
                            </span>
                          </ChapterMeta>
                        </ChapterInfo>
                        <ChapterActions>
                          <SmallIconBtn onClick={(e) => { e.stopPropagation(); handleEditChapter(chapter._id); }} title="编辑">
                            ✏️
                          </SmallIconBtn>
                          <SmallIconBtn onClick={(e) => { e.stopPropagation(); handleDeleteChapter(chapter._id); }} title="删除">
                            🗑️
                          </SmallIconBtn>
                          <SmallIconBtn onClick={(e) => { e.stopPropagation(); handleAddSection(chapter._id); }} title="添加小节">
                            ➕
                          </SmallIconBtn>
                          <SmallIconBtn onClick={(e) => { e.stopPropagation(); toggleChapterExpanded(chapter._id); }} title="展开/收起">
                            {expandedChapters.has(chapter._id) ? '▼' : '▶'}
                          </SmallIconBtn>
                        </ChapterActions>
                      </ChapterHeader>

                      <ChapterContent $expanded={expandedChapters.has(chapter._id)}>
                        {chapter.sections && chapter.sections.length > 0 ? (
                          <SectionList>
                            {chapter.sections.map((section) => (
                              <SectionItem
                                key={section._id}
                                $isOver={dropTarget?.sectionId === section._id}
                              >
                                <SectionHeader>
                                  <SectionTitle>{section.title}</SectionTitle>
                                  <ChapterActions>
                                    <SmallIconBtn onClick={() => handleEditSection(chapter._id, section._id)} title="编辑">
                                      ✏️
                                    </SmallIconBtn>
                                    <SmallIconBtn onClick={() => handleDeleteSection(chapter._id, section._id)} title="删除">
                                      🗑️
                                    </SmallIconBtn>
                                  </ChapterActions>
                                </SectionHeader>
                                <TopicsContainer
                                  $isOver={dropTarget?.sectionId === section._id}
                                  onDragOver={(e) => {
                                    e.preventDefault();
                                    handleDragOver(e, { type: 'section', chapterId: chapter._id, sectionId: section._id });
                                  }}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => {
                                    // Use dropTarget if it has insertIndex, otherwise use basic target
                                    const target = dropTarget?.sectionId === section._id && dropTarget?.insertIndex !== undefined
                                      ? dropTarget
                                      : { type: 'section' as const, chapterId: chapter._id, sectionId: section._id };
                                    handleDrop(e, target);
                                  }}
                                >
                                  {section.topics.length === 0 ? (
                                    <span style={{ fontSize: '0.75rem', color: glassTheme.colors.text.muted }}>
                                      拖拽知识元到此处
                                    </span>
                                  ) : (
                                    <>
                                      {section.topics.map((topicRef: any, topicIndex) => {
                                        // New schema: topicRef = { topicId: ObjectId, activities: {...} }
                                        // Extract topicId from the object using helper function
                                        const topicIdStr = getTopicIdString(topicRef);
                                        const topic = allTopics.find(t => t._id === topicIdStr || t.id === topicIdStr);
                                        if (!topic) {
                                          return null;
                                        }
                                        const actualTopicId = topic._id;
                                        const isDraggingThis = draggedItem?.topic._id === actualTopicId;
                                        const showDropAfter = dropTarget?.sectionId === section._id &&
                                                           dropTarget?.insertIndex === topicIndex + 1 &&
                                                           !isDraggingThis;

                                        // Get activities for this topic
                                        const topicActivities = getTopicActivities(topicRef);
                                        const hasActivities = topicActivities.length > 0;

                                        return (
                                          <React.Fragment key={actualTopicId}>
                                            <TopicChip
                                              data-topic-id={actualTopicId}
                                              draggable
                                              $isDragging={isDraggingThis}
                                              onDragStart={() => handleDragStart(topic, 'section', chapter._id, section._id, topicIndex)}
                                              onDragOver={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                handleDragOver(e, {
                                                  type: 'section',
                                                  chapterId: chapter._id,
                                                  sectionId: section._id,
                                                  insertIndex: topicIndex
                                                });
                                              }}
                                              style={{
                                                opacity: isDraggingThis ? 0.5 : 1,
                                                border: showDropAfter ? '2px dashed #60a5fa' : undefined
                                              }}
                                            >
                                              <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '0.4rem' }}>
                                                <TopicDragHandle>⋮⋮</TopicDragHandle>
                                                <TopicSequence>{topicIndex + 1}</TopicSequence>
                                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{topic.title}</span>
                                                <ActivityDropdownContainer>
                                                  <ActivityDropdownBtn
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      const container = (e.target as HTMLElement).closest('.activity-dropdown-container') as HTMLDivElement;
                                                      toggleActivityDropdown(actualTopicId, container);
                                                    }}
                                                  >
                                                    ➕ 活动 ▾
                                                  </ActivityDropdownBtn>
                                                </ActivityDropdownContainer>
                                                <PortalDropdown
                                                  isOpen={openActivityDropdown === actualTopicId}
                                                  onClose={() => { setOpenActivityDropdown(null); setAddActivityDropdownAnchor(null); }}
                                                  anchorElement={addActivityDropdownAnchor}
                                                >
                                                  {(() => {
                                                    // Show 4 activity types (users can add multiple of same type)
                                                    const allActivityTypes = [
                                                      { type: 'reading', label: '📖 阅读内容' },
                                                      { type: 'video', label: '🎬 添加视频' },
                                                      { type: 'selfExplanation', label: '💭 添加 Self Explanation' },
                                                      { type: 'exercise', label: '📝 添加练习' }
                                                    ];
                                                    return allActivityTypes.map(a => (
                                                      <ActivityDropdownItem key={a.type} onClick={() => {
                                                        setOpenActivityDropdown(null);
                                                        if (a.type === 'reading') {
                                                          // Open reading modal
                                                          setReadingUseTopicContent(false);
                                                          setReadingModalContent('');
                                                          setReadingModalLink('');
                                                          setReadingModalContext({ topicId: actualTopicId, chapterId: chapter._id, sectionId: section._id });
                                                          setShowReadingModal(true);
                                                        } else if (a.type === 'video') {
                                                          // Open video modal
                                                          setVideoModalTitle('');
                                                          setVideoModalUrl('');
                                                          setVideoModalContext({ topicId: actualTopicId, chapterId: chapter._id, sectionId: section._id });
                                                          setShowVideoModal(true);
                                                        } else if (a.type === 'exercise') {
                                                          // Exercise defaults to "习题" title
                                                          handleAddActivity(a.type, actualTopicId, chapter._id, section._id, '习题');
                                                        } else {
                                                          const title = window.prompt('请输入活动标题:', a.label.replace(/^[^\s]+\s/, ''));
                                                          if (title !== null) {
                                                            handleAddActivity(a.type, actualTopicId, chapter._id, section._id, title);
                                                          }
                                                        }
                                                      }}>
                                                        {a.label}
                                                      </ActivityDropdownItem>
                                                    ));
                                                  })()}
                                                </PortalDropdown>
                                                {hasActivities && (
                                                  <ActivitiesToggleContainer>
                                                    <ActivitiesToggleBtn
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        const container = (e.target as HTMLElement).closest('.activities-toggle-container') as HTMLDivElement;
                                                        toggleActivitiesPanel(actualTopicId, container);
                                                      }}
                                                    >
                                                      📚 学习活动 ({topicActivities.length}) ▾
                                                    </ActivitiesToggleBtn>
                                                  </ActivitiesToggleContainer>
                                                )}
                                                <PortalDropdown
                                                  isOpen={expandedActivitiesPanels.has(actualTopicId)}
                                                  onClose={() => toggleActivitiesPanel(actualTopicId)}
                                                  anchorElement={activitiesDropdownAnchor}
                                                >
                                                  <LearningActivitiesDropdownHeader>
                                                    学习活动 (可拖拽排序)
                                                  </LearningActivitiesDropdownHeader>
                                                  <LearningActivitiesList>
                                                    {topicActivities.map((activity, idx) => (
                                                      <LearningActivityItem
                                                        key={activity.id}
                                                        draggable
                                                        $isDragging={draggedActivity?.topicId === actualTopicId && draggedActivity?.activityIndex === idx}
                                                        $isDropTarget={activityDropIndex === idx && draggedActivity?.topicId === actualTopicId && draggedActivity?.activityIndex !== idx}
                                                        onDragStart={() => setDraggedActivity({ topicId: actualTopicId, activityIndex: idx, activityType: activity.type })}
                                                        onDragOver={(e) => {
                                                          e.preventDefault();
                                                          setActivityDropIndex(idx);
                                                        }}
                                                        onDragLeave={() => setActivityDropIndex(null)}
                                                        onDrop={(e) => {
                                                          e.preventDefault();
                                                          if (draggedActivity && draggedActivity.topicId === actualTopicId) {
                                                            handleActivityDrop(actualTopicId, draggedActivity.activityIndex, idx, chapter._id, section._id);
                                                          }
                                                          setDraggedActivity(null);
                                                          setActivityDropIndex(null);
                                                        }}
                                                        onDragEnd={() => {
                                                          setDraggedActivity(null);
                                                          setActivityDropIndex(null);
                                                        }}
                                                      >
                                                        <ActivityDragHandle>⋮⋮</ActivityDragHandle>
                                                        <span>{activity.icon} {activity.title || activity.label}</span>
                                                        <ActivityRemoveBtn onClick={(e) => { e.stopPropagation(); handleRemoveActivity(activity.id, actualTopicId, chapter._id, section._id); }}>
                                                          ✕
                                                        </ActivityRemoveBtn>
                                                      </LearningActivityItem>
                                                    ))}
                                                  </LearningActivitiesList>
                                                </PortalDropdown>
                                                <TopicRemoveBtn onClick={() => handleRemoveTopic(actualTopicId, chapter._id, section._id)}>
                                                  ✕
                                                </TopicRemoveBtn>
                                              </div>
                                            </TopicChip>
                                            {showDropAfter && (
                                              <div style={{
                                                width: '100%',
                                                height: '2px',
                                                background: '#60a5fa',
                                                margin: '2px 0',
                                                borderRadius: '1px'
                                              }} />
                                            )}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  )}
                                </TopicsContainer>
                              </SectionItem>
                            ))}
                          </SectionList>
                        ) : (
                          <TopicsContainer
                            $isOver={dropTarget?.chapterId === chapter._id && !dropTarget?.sectionId}
                            onDragOver={(e) => {
                              e.preventDefault();
                              handleDragOver(e, { type: 'chapter', chapterId: chapter._id });
                            }}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => {
                              // Use dropTarget if it has insertIndex, otherwise use basic target
                              const target = dropTarget?.chapterId === chapter._id && !dropTarget?.sectionId && dropTarget?.insertIndex !== undefined
                                ? dropTarget
                                : { type: 'chapter' as const, chapterId: chapter._id };
                              handleDrop(e, target);
                            }}
                          >
                            {chapter.topics.length === 0 ? (
                              <span style={{ fontSize: '0.75rem', color: glassTheme.colors.text.muted }}>
                                拖拽知识元到此处，或添加小节
                              </span>
                            ) : (
                              chapter.topics.map((topicRef: any, topicIndex) => {
                                // New schema: topicRef = { topicId: ObjectId, activities: {...} }
                                // Extract topicId from the object using helper function
                                const topicIdStr = getTopicIdString(topicRef);
                                const topic = allTopics.find(t => t._id === topicIdStr || t.id === topicIdStr);
                                if (!topic) return null;
                                const actualTopicId = topic._id;
                                const isDraggingThis = draggedItem?.topic._id === actualTopicId;
                                const showDropAfter = dropTarget?.chapterId === chapter._id &&
                                                   !dropTarget?.sectionId &&
                                                   dropTarget?.insertIndex === topicIndex + 1 &&
                                                   !isDraggingThis;

                                // Get activities for this topic
                                const topicActivities = getTopicActivities(topicRef);
                                const hasActivities = topicActivities.length > 0;

                                return (
                                  <React.Fragment key={actualTopicId}>
                                    <TopicChip
                                      data-topic-id={actualTopicId}
                                      draggable
                                      $isDragging={isDraggingThis}
                                      onDragStart={() => handleDragStart(topic, 'chapter', chapter._id, undefined, topicIndex)}
                                      onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDragOver(e, {
                                          type: 'chapter',
                                          chapterId: chapter._id,
                                          insertIndex: topicIndex
                                        });
                                      }}
                                      style={{
                                        opacity: isDraggingThis ? 0.5 : 1,
                                        border: showDropAfter ? '2px dashed #60a5fa' : undefined,
                                        flexDirection: 'column',
                                        alignItems: 'flex-start'
                                      }}
                                    >
                                      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '0.4rem' }}>
                                        <TopicDragHandle>⋮⋮</TopicDragHandle>
                                        <TopicSequence>{topicIndex + 1}</TopicSequence>
                                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{topic.title}</span>
                                        <ActivityDropdownContainer>
                                          <ActivityDropdownBtn
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const container = (e.target as HTMLElement).closest('.activity-dropdown-container') as HTMLDivElement;
                                              toggleActivityDropdown(actualTopicId, container);
                                            }}
                                          >
                                            ➕ 活动 ▾
                                          </ActivityDropdownBtn>
                                        </ActivityDropdownContainer>
                                        <PortalDropdown
                                          isOpen={openActivityDropdown === actualTopicId}
                                          onClose={() => { setOpenActivityDropdown(null); setAddActivityDropdownAnchor(null); }}
                                          anchorElement={addActivityDropdownAnchor}
                                        >
                                          {(() => {
                                            // Show 4 activity types (users can add multiple of same type)
                                            const allActivityTypes = [
                                              { type: 'reading', label: '📖 阅读内容' },
                                              { type: 'video', label: '🎬 添加视频' },
                                              { type: 'selfExplanation', label: '💭 添加 Self Explanation' },
                                              { type: 'exercise', label: '📝 添加练习' }
                                            ];
                                            return allActivityTypes.map(a => (
                                              <ActivityDropdownItem key={a.type} onClick={() => {
                                                setOpenActivityDropdown(null);
                                                if (a.type === 'reading') {
                                                  // Open reading modal
                                                  setReadingUseTopicContent(false);
                                                  setReadingModalContent('');
                                                  setReadingModalLink('');
                                                  setReadingModalContext({ topicId: actualTopicId, chapterId: chapter._id });
                                                  setShowReadingModal(true);
                                                } else if (a.type === 'video') {
                                                  // Open video modal
                                                  setVideoModalTitle('');
                                                  setVideoModalUrl('');
                                                  setVideoModalContext({ topicId: actualTopicId, chapterId: chapter._id });
                                                  setShowVideoModal(true);
                                                } else if (a.type === 'exercise') {
                                                  // Exercise defaults to "习题" title
                                                  handleAddActivity(a.type, actualTopicId, chapter._id, undefined, '习题');
                                                } else {
                                                  const title = window.prompt('请输入活动标题:', a.label.replace(/^[^\s]+\s/, ''));
                                                  if (title !== null) {
                                                    handleAddActivity(a.type, actualTopicId, chapter._id, undefined, title);
                                                  }
                                                }
                                              }}>
                                                {a.label}
                                              </ActivityDropdownItem>
                                            ));
                                          })()}
                                        </PortalDropdown>
                                        {hasActivities && (
                                          <ActivitiesToggleContainer>
                                            <ActivitiesToggleBtn
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                const container = (e.target as HTMLElement).closest('.activities-toggle-container') as HTMLDivElement;
                                                toggleActivitiesPanel(actualTopicId, container);
                                              }}
                                            >
                                              📚 学习活动 ({topicActivities.length}) ▾
                                            </ActivitiesToggleBtn>
                                          </ActivitiesToggleContainer>
                                        )}
                                        <PortalDropdown
                                          isOpen={expandedActivitiesPanels.has(actualTopicId)}
                                          onClose={() => toggleActivitiesPanel(actualTopicId)}
                                          anchorElement={activitiesDropdownAnchor}
                                        >
                                          <LearningActivitiesDropdownHeader>
                                            学习活动 (可拖拽排序)
                                          </LearningActivitiesDropdownHeader>
                                          <LearningActivitiesList>
                                            {topicActivities.map((activity, idx) => (
                                              <LearningActivityItem
                                                key={activity.type}
                                                draggable
                                                $isDragging={draggedActivity?.topicId === actualTopicId && draggedActivity?.activityIndex === idx}
                                                $isDropTarget={activityDropIndex === idx && draggedActivity?.topicId === actualTopicId && draggedActivity?.activityIndex !== idx}
                                                onDragStart={() => setDraggedActivity({ topicId: actualTopicId, activityIndex: idx, activityType: activity.type })}
                                                onDragOver={(e) => {
                                                  e.preventDefault();
                                                  setActivityDropIndex(idx);
                                                }}
                                                onDragLeave={() => setActivityDropIndex(null)}
                                                onDrop={(e) => {
                                                  e.preventDefault();
                                                  if (draggedActivity && draggedActivity.topicId === actualTopicId) {
                                                    handleActivityDrop(actualTopicId, draggedActivity.activityIndex, idx, chapter._id);
                                                  }
                                                  setDraggedActivity(null);
                                                  setActivityDropIndex(null);
                                                }}
                                                onDragEnd={() => {
                                                  setDraggedActivity(null);
                                                  setActivityDropIndex(null);
                                                }}
                                              >
                                                <ActivityDragHandle>⋮⋮</ActivityDragHandle>
                                                <span>{activity.icon} {activity.title || activity.label}</span>
                                                <ActivityRemoveBtn onClick={(e) => { e.stopPropagation(); handleRemoveActivity(activity.id, actualTopicId, chapter._id); }}>
                                                  ✕
                                                </ActivityRemoveBtn>
                                              </LearningActivityItem>
                                            ))}
                                          </LearningActivitiesList>
                                        </PortalDropdown>
                                        <TopicRemoveBtn onClick={() => handleRemoveTopic(actualTopicId, chapter._id)}>
                                          ✕
                                        </TopicRemoveBtn>
                                      </div>
                                    </TopicChip>
                                    {showDropAfter && (
                                      <div style={{
                                        width: '100%',
                                        height: '2px',
                                        background: '#60a5fa',
                                        margin: '2px 0',
                                        borderRadius: '1px'
                                      }} />
                                    )}
                                  </React.Fragment>
                                );
                              })
                            )}
                          </TopicsContainer>
                        )}

                        {(!chapter.sections || chapter.sections.length === 0) && (
                          <AddSectionBtn onClick={() => handleAddSection(chapter._id)}>
                            ➕ 添加小节
                          </AddSectionBtn>
                        )}
                      </ChapterContent>
                    </ChapterItem>
                  ))}
                </ChapterList>
              )}
            </StructureContent>

            <StatsPanel>
              <StatItem>
                <StatValue>{Array.isArray(course?.enrolledStudents) ? course?.enrolledStudents.length : 0}</StatValue>
                <StatLabel>学生</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>{course?.chapters.length || 0}</StatValue>
                <StatLabel>章节</StatLabel>
              </StatItem>
              <StatItem>
                <StatValue>{course?.totalTopics || 0}</StatValue>
                <StatLabel>知识元</StatLabel>
              </StatItem>
            </StatsPanel>
          </StructurePanel>
        </LeftPanel>

        {/* Right Panel - Topics Library */}
        <RightPanel>
          <TopicsLibraryHeader>
            <TopicsLibraryTitle>📚 知识元库</TopicsLibraryTitle>
            <SearchInput
              placeholder="搜索知识元..."
              value={topicsSearch}
              onChange={(e) => setTopicsSearch(e.target.value)}
            />
          </TopicsLibraryHeader>

          <TopicsLibraryContent>
            {loadingTopics ? (
              <LoadingSpinner>加载中...</LoadingSpinner>
            ) : filteredTopics.length === 0 ? (
              <EmptyState>没有找到匹配的知识元</EmptyState>
            ) : (
              filteredTopics.map((topic) => {
                const isInCourse = courseTopicIds.has(topic._id);
                return (
                  <TopicCard
                    key={topic._id}
                    data-library-topic-id={topic._id}
                    draggable
                    $inCourse={isInCourse}
                    onDragStart={() => handleDragStart(topic, 'browser')}
                  >
                    <TopicCardTitle>{topic.title}</TopicCardTitle>
                    <TopicCardMeta>
                      {isInCourse && <Badge $color="green">✓ 已添加</Badge>}
                    </TopicCardMeta>
                  </TopicCard>
                );
              })
            )}
          </TopicsLibraryContent>
        </RightPanel>
      </PageContainer>

      {/* Course Info Edit Modal */}
      {showEditModal && (
        <ModalOverlay onClick={closeEditModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>编辑课程信息</ModalTitle>

            <ModalField>
              <ModalLabel>课程标题 *</ModalLabel>
              <ModalInput
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                placeholder="例如：统计学基础"
                autoFocus
              />
            </ModalField>

            <ModalField>
              <ModalLabel>课程描述 *</ModalLabel>
              <ModalTextarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder="描述课程的主要内容、学习目标等..."
              />
            </ModalField>

            <ModalActions>
              <IconButton $variant="secondary" onClick={closeEditModal}>
                取消
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={saveCourseInfo}
                disabled={saving || !editTitle.trim()}
              >
                {saving ? '保存中...' : '保存'}
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Chapter Modal */}
      {showChapterModal && (
        <ModalOverlay onClick={closeChapterModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {chapterModalMode === 'add' ? '添加章节' : '编辑章节'}
            </ModalTitle>

            <ModalField>
              <ModalLabel>章节标题 *</ModalLabel>
              <ModalInput
                type="text"
                value={chapterModalTitle}
                onChange={(e) => setChapterModalTitle(e.target.value)}
                placeholder="例如：第一章 统计学基础"
                autoFocus
              />
            </ModalField>

            <ModalField>
              <ModalLabel>章节描述</ModalLabel>
              <ModalTextarea
                value={chapterModalDescription}
                onChange={(e) => setChapterModalDescription(e.target.value)}
                placeholder="描述本章的主要内容..."
                rows={3}
              />
            </ModalField>

            <ModalActions>
              <IconButton $variant="secondary" onClick={closeChapterModal}>
                取消
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={saveChapter}
                disabled={!chapterModalTitle.trim()}
              >
                {chapterModalMode === 'add' ? '添加' : '保存'}
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Section Modal */}
      {showSectionModal && (
        <ModalOverlay onClick={closeSectionModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>
              {sectionModalMode === 'add' ? '添加小节' : '编辑小节'}
            </ModalTitle>

            <ModalField>
              <ModalLabel>小节标题 *</ModalLabel>
              <ModalInput
                type="text"
                value={sectionModalTitle}
                onChange={(e) => setSectionModalTitle(e.target.value)}
                placeholder="例如：1.1 统计学的定义"
                autoFocus
              />
            </ModalField>

            <ModalActions>
              <IconButton $variant="secondary" onClick={closeSectionModal}>
                取消
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={saveSection}
                disabled={!sectionModalTitle.trim()}
              >
                {sectionModalMode === 'add' ? '添加' : '保存'}
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <ModalOverlay onClick={() => {
          setShowVideoModal(false);
          setVideoPreviewMode(false);
        }}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: videoPreviewMode ? '600px' : '400px' }}>
            <ModalTitle>🎬 添加视频</ModalTitle>

            {/* Input Mode */}
            {!videoPreviewMode && (
              <>
                <ModalField>
                  <ModalLabel>视频标题 *</ModalLabel>
                  <ModalInput
                    type="text"
                    value={videoModalTitle}
                    onChange={(e) => setVideoModalTitle(e.target.value)}
                    placeholder="请输入视频标题"
                  />
                </ModalField>

                <ModalField>
                  <ModalLabel>视频链接 *</ModalLabel>
                  <ModalInput
                    type="text"
                    value={videoModalUrl}
                    onChange={(e) => setVideoModalUrl(e.target.value)}
                    placeholder="请插入视频链接（支持 Bilibili、YouTube 等）"
                  />
                </ModalField>
              </>
            )}

            {/* Preview Mode */}
            {videoPreviewMode && (
              <>
                <ModalField>
                  <ModalLabel>视频标题</ModalLabel>
                  <div style={{ color: '#fff', fontSize: '1.1rem', fontWeight: 600, padding: '0.5rem 0' }}>
                    {videoModalTitle}
                  </div>
                </ModalField>

                <ModalField>
                  <ModalLabel>视频预览</ModalLabel>
                  <div style={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%',
                    backgroundColor: '#000',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    marginTop: '0.5rem'
                  }}>
                    {getVideoEmbedUrl(videoModalUrl) ? (
                      <iframe
                        src={getVideoEmbedUrl(videoModalUrl) || ''}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 'none'
                        }}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        color: '#888',
                        textAlign: 'center'
                      }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>⚠️</div>
                        <div>无法解析视频链接</div>
                        <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>请检查链接格式</div>
                      </div>
                    )}
                  </div>
                </ModalField>
              </>
            )}

            <ModalActions>
              <IconButton
                $variant="secondary"
                onClick={() => {
                  if (videoPreviewMode) {
                    setVideoPreviewMode(false);
                  } else {
                    setShowVideoModal(false);
                  }
                }}
              >
                {videoPreviewMode ? '返回修改' : '取消'}
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={handleVideoModalSubmit}
                disabled={!videoModalTitle.trim() || !videoModalUrl.trim()}
              >
                {videoPreviewMode ? '确定保存' : '预览视频'}
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Reading Modal */}
      {showReadingModal && (
        <ModalOverlay onClick={() => setShowReadingModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: '450px' }}>
            <ModalTitle>📖 添加阅读活动</ModalTitle>

            <ModalField>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={readingUseTopicContent}
                  onChange={(e) => setReadingUseTopicContent(e.target.checked)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span style={{ color: '#fff', fontSize: '14px' }}>阅读本知识元内容</span>
              </label>
            </ModalField>

            <ModalField>
              <ModalLabel>阅读标题</ModalLabel>
              <ModalInput
                type="text"
                value={readingModalContent}
                onChange={(e) => setReadingModalContent(e.target.value)}
                placeholder="请输入阅读标题"
                disabled={readingUseTopicContent}
                style={{
                  opacity: readingUseTopicContent ? 0.5 : 1,
                  cursor: readingUseTopicContent ? 'not-allowed' : 'text'
                }}
              />
            </ModalField>

            <ModalField>
              <ModalLabel>阅读链接 *</ModalLabel>
              <ModalInput
                type="text"
                value={readingModalLink}
                onChange={(e) => setReadingModalLink(e.target.value)}
                placeholder="请输入阅读链接"
                disabled={readingUseTopicContent}
                style={{
                  opacity: readingUseTopicContent ? 0.5 : 1,
                  cursor: readingUseTopicContent ? 'not-allowed' : 'text'
                }}
              />
            </ModalField>

            <ModalActions>
              <IconButton
                $variant="secondary"
                onClick={() => setShowReadingModal(false)}
              >
                取消
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={handleReadingModalSubmit}
                disabled={!readingUseTopicContent && !readingModalLink.trim()}
              >
                确定
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {/* Survey Modal */}
      {showSurveyModal && (
        <ModalOverlay onClick={() => setShowSurveyModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ minWidth: '450px' }}>
            <ModalTitle>📊 添加问卷测量</ModalTitle>

            {/* Mode Selection */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
              <button
                onClick={() => setSurveyModalMode('select')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: surveyModalMode === 'select' ? '2px solid #5d8aa8' : '1px solid #444',
                  borderRadius: '8px',
                  background: surveyModalMode === 'select' ? 'rgba(93, 138, 168, 0.2)' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                📚 从数据库中挑选
              </button>
              <button
                onClick={() => setSurveyModalMode('input')}
                style={{
                  flex: 1,
                  padding: '12px',
                  border: surveyModalMode === 'input' ? '2px solid #5d8aa8' : '1px solid #444',
                  borderRadius: '8px',
                  background: surveyModalMode === 'input' ? 'rgba(93, 138, 168, 0.2)' : 'transparent',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                ✏️ 输入问卷
              </button>
            </div>

            {/* Select Mode */}
            {surveyModalMode === 'select' && (
              <ModalField>
                <ModalLabel>选择问卷</ModalLabel>
                <select
                  value={selectedSurveyId}
                  onChange={(e) => setSelectedSurveyId(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px',
                    borderRadius: '8px',
                    border: '1px solid #444',
                    background: '#1e1e2e',
                    color: '#fff',
                    fontSize: '14px'
                  }}
                >
                  <option value="">-- 请选择问卷 --</option>
                  {availableSurveys.map(survey => (
                    <option key={survey._id} value={survey._id}>
                      {survey.title} {survey.category ? `(${survey.category})` : ''}
                    </option>
                  ))}
                </select>
                {availableSurveys.length === 0 && (
                  <div style={{ color: '#888', fontSize: '12px', marginTop: '8px' }}>
                    数据库中暂无问卷，请选择"输入问卷"手动添加
                  </div>
                )}
              </ModalField>
            )}

            {/* Input Mode */}
            {surveyModalMode === 'input' && (
              <>
                <ModalField>
                  <ModalLabel>问卷标题 *</ModalLabel>
                  <ModalInput
                    type="text"
                    value={surveyInputTitle}
                    onChange={(e) => setSurveyInputTitle(e.target.value)}
                    placeholder="请输入问卷标题"
                  />
                </ModalField>

                <ModalField>
                  <ModalLabel>问卷链接 *</ModalLabel>
                  <ModalInput
                    type="text"
                    value={surveyInputLink}
                    onChange={(e) => setSurveyInputLink(e.target.value)}
                    placeholder="请输入问卷链接（如问卷星、腾讯问卷等）"
                  />
                </ModalField>

                <ModalField>
                  <ModalLabel>问卷描述</ModalLabel>
                  <textarea
                    value={surveyInputDescription}
                    onChange={(e) => setSurveyInputDescription(e.target.value)}
                    placeholder="请输入问卷描述（选填）"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      background: '#1e1e2e',
                      color: '#fff',
                      fontSize: '14px',
                      minHeight: '80px',
                      resize: 'vertical'
                    }}
                  />
                </ModalField>

                <ModalField>
                  <ModalLabel>计分规则</ModalLabel>
                  <textarea
                    value={surveyInputScoring}
                    onChange={(e) => setSurveyInputScoring(e.target.value)}
                    placeholder="请输入计分规则（选填）"
                    style={{
                      width: '100%',
                      padding: '12px',
                      borderRadius: '8px',
                      border: '1px solid #444',
                      background: '#1e1e2e',
                      color: '#fff',
                      fontSize: '14px',
                      minHeight: '60px',
                      resize: 'vertical'
                    }}
                  />
                </ModalField>
              </>
            )}

            <ModalActions>
              <IconButton
                $variant="secondary"
                onClick={() => setShowSurveyModal(false)}
              >
                取消
              </IconButton>
              <IconButton
                $variant="primary"
                onClick={handleSurveyModalSubmit}
                disabled={
                  surveyModalMode === 'select'
                    ? !selectedSurveyId
                    : !surveyInputTitle.trim() || !surveyInputLink.trim()
                }
              >
                确定
              </IconButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </DashboardLayout>
  );
};

export default CourseEditPage;
